import React, { useState, forwardRef, useImperativeHandle } from 'react';
import { api } from '../../api';
import { toast } from 'sonner';
import {
  ShieldX, Plus, Trash2, Edit2, X, ChevronDown, ChevronUp,
  ToggleLeft, ToggleRight, Info, Gauge, Users,
} from 'lucide-react';

const EFFECT_COLORS = {
  ALLOW: { bg: 'bg-emerald-50 border-emerald-200', badge: 'bg-emerald-100 text-emerald-800', dot: 'bg-emerald-500' },
  DENY:  { bg: 'bg-red-50 border-red-200',         badge: 'bg-red-100 text-red-800',         dot: 'bg-red-500'     },
};

const EmptyAccessRuleForm = {
  name: '', description: '', effect: 'ALLOW', match_all: true, conditions: [], is_active: true, priority: 0, max_users: '',
};

/**
 * Access Rules tab: rule list (with capacity/waitlist seats panel) + create/edit modal.
 * Parent (AccessControl.jsx) owns `rules` (needed for the tab badge count and the
 * "no ALLOW rules" warning banner) — this component mutates it via `setRules` for
 * optimistic updates, and calls `reload()` after CRUD writes that need fresh server data.
 * `openCreate` is exposed via ref so the shared header "Add Rule" button can trigger it.
 */
const AccessRulesTab = forwardRef(function AccessRulesTab({ rules, setRules, config, reload }, ref) {
  const [showForm, setShowForm]       = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [form, setForm]               = useState(EmptyAccessRuleForm);
  const [saving, setSaving]           = useState(false);
  const [expanded, setExpanded]       = useState({});
  const [seatsData, setSeatsData]     = useState({});
  const [loadingSeats, setLoadingSeats] = useState({});
  const [freeingSeat, setFreeingSeat] = useState(null);

  const openCreate = () => { setEditingRule(null); setForm(EmptyAccessRuleForm); setShowForm(true); };
  const openEdit   = (rule) => { setEditingRule(rule); setForm({ ...rule, max_users: rule.max_users ?? '' }); setShowForm(true); };

  useImperativeHandle(ref, () => ({ openCreate }));

  const addCondition = () => setForm(f => ({
    ...f, conditions: [...f.conditions, { field: config.token_fields[0]?.field || 'email', operator: 'equals', value: '' }]
  }));
  const removeCondition = (i) => setForm(f => ({ ...f, conditions: f.conditions.filter((_, idx) => idx !== i) }));
  const updateCondition = (i, key, val) => setForm(f => ({
    ...f, conditions: f.conditions.map((c, idx) => idx === i ? { ...c, [key]: val } : c)
  }));

  const saveRule = async () => {
    if (!form.name.trim()) { toast.error('Rule name is required'); return; }
    if (!form.conditions.length) { toast.error('Add at least one condition'); return; }
    for (const c of form.conditions) {
      if (!c.value.trim()) { toast.error('All condition values must be filled'); return; }
    }
    setSaving(true);
    const payload = { ...form, max_users: form.max_users === '' ? null : parseInt(form.max_users, 10) };
    try {
      if (editingRule) {
        await api.request(`/admin/access-rules/${editingRule.id}`, { method: 'PUT', body: JSON.stringify(payload) });
        toast.success('Rule updated');
      } else {
        await api.request('/admin/access-rules', { method: 'POST', body: JSON.stringify(payload) });
        toast.success('Rule created');
      }
      setShowForm(false); reload();
    } catch { toast.error('Failed to save rule'); }
    finally { setSaving(false); }
  };

  const deleteRule = async (id) => {
    if (!window.confirm('Delete this rule?')) return;
    try {
      await api.request(`/admin/access-rules/${id}`, { method: 'DELETE' });
      toast.success('Rule deleted');
      setRules(r => r.filter(x => x.id !== id));
    } catch { toast.error('Failed to delete rule'); }
  };

  const toggleRule = async (rule) => {
    try {
      await api.request(`/admin/access-rules/${rule.id}`, { method: 'PUT', body: JSON.stringify({ is_active: !rule.is_active }) });
      setRules(r => r.map(x => x.id === rule.id ? { ...x, is_active: !x.is_active } : x));
    } catch { toast.error('Failed to toggle rule'); }
  };

  const loadSeats = async (ruleId) => {
    setLoadingSeats(s => ({ ...s, [ruleId]: true }));
    try {
      const data = await api.request(`/admin/access-rules/${ruleId}/seats`);
      setSeatsData(s => ({ ...s, [ruleId]: data }));
      return data;
    } catch {
      toast.error('Failed to load seat usage');
      return null;
    } finally {
      setLoadingSeats(s => ({ ...s, [ruleId]: false }));
    }
  };

  // Refresh the collapsed rule card's badge counts from the authoritative seats/waitlist
  // response (never guess optimistically — freeing a seat can auto-promote a waitlisted
  // user into that same seat, so seats_used may stay the same while waitlist drops).
  const syncRuleCounts = (ruleId, data) => {
    if (!data) return;
    setRules(r => r.map(x => x.id === ruleId
      ? { ...x, seats_used: data.seats.length, waitlist_count: data.waitlist.length }
      : x));
  };

  const freeSeat = async (ruleId, email) => {
    const key = `${ruleId}-${email}`;
    setFreeingSeat(key);
    try {
      await api.request(`/admin/access-rules/${ruleId}/seats/${encodeURIComponent(email)}`, { method: 'DELETE' });
      toast.success(`Seat freed for ${email}`);
      syncRuleCounts(ruleId, await loadSeats(ruleId));
    } catch { toast.error('Failed to free seat'); }
    finally { setFreeingSeat(null); }
  };

  const promoteWaitlisted = async (ruleId, email) => {
    const key = `promote-${ruleId}-${email}`;
    setFreeingSeat(key);
    try {
      await api.request(`/admin/access-rules/${ruleId}/waitlist/${encodeURIComponent(email)}/promote`, { method: 'POST' });
      toast.success(`Seat granted to ${email}`);
      syncRuleCounts(ruleId, await loadSeats(ruleId));
    } catch (e) { toast.error(e.message || 'Failed to promote — check if a seat is actually free'); }
    finally { setFreeingSeat(null); }
  };

  return (
    <>
      {/* Token fields info */}
      <div className="p-4 bg-blue-50 border border-blue-100 rounded-xl">
        <div className="flex items-start gap-2">
          <Info size={16} className="text-blue-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-blue-800 mb-2">LevelUp SSO Token Fields Available</p>
            <div className="flex flex-wrap gap-2">
              {config.token_fields.map(f => (
                <span key={f.field} className="bg-white border border-blue-200 text-blue-700 text-xs px-2 py-1 rounded-full">
                  <strong>{f.field}</strong> — {f.label} <span className="text-blue-400">e.g. {f.example}</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {rules.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-xl">
          <ShieldX size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500 font-medium">No access rules yet</p>
          <p className="text-gray-400 text-sm mt-1">Create rules to control who can access the portal via SSO</p>
          <button onClick={openCreate} className="mt-4 px-4 py-2 bg-levelup-accent text-levelup-darker rounded-lg text-sm font-bold">
            Create First Rule
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {rules.map(rule => {
            const colors = EFFECT_COLORS[rule.effect] || EFFECT_COLORS.ALLOW;
            const isExpanded = expanded[rule.id];
            return (
              <div
                key={rule.id}
                data-testid={`rule-${rule.id}`}
                className={`border rounded-xl overflow-hidden transition-all ${colors.bg} ${!rule.is_active ? 'opacity-50' : ''}`}
              >
                <div className="flex items-center gap-3 p-4">
                  <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${colors.dot}`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-800">{rule.name}</span>
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${colors.badge}`}>{rule.effect}</span>
                      {!rule.is_active && <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">Disabled</span>}
                      {!!rule.max_users && (
                        <span
                          data-testid={`capacity-badge-${rule.id}`}
                          className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full ${
                            (rule.seats_used || 0) >= rule.max_users ? 'bg-red-100 text-red-700' : 'bg-indigo-100 text-indigo-700'
                          }`}
                        >
                          <Gauge size={11} /> {rule.seats_used || 0} / {rule.max_users} seats
                        </span>
                      )}
                      {!!rule.waitlist_count && (
                        <span
                          data-testid={`waitlist-badge-${rule.id}`}
                          className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700"
                        >
                          <Users size={11} /> {rule.waitlist_count} waiting
                        </span>
                      )}
                    </div>
                    {rule.description && <p className="text-xs text-gray-500 mt-0.5">{rule.description}</p>}
                    <p className="text-xs text-gray-500 mt-1">
                      {rule.conditions.length} condition{rule.conditions.length !== 1 ? 's' : ''} —
                      match <strong>{rule.match_all ? 'ALL' : 'ANY'}</strong> — priority {rule.priority}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button data-testid={`toggle-rule-${rule.id}`} onClick={() => toggleRule(rule)}
                      className="p-2 rounded-lg hover:bg-white/60 text-gray-500 transition-colors"
                      title={rule.is_active ? 'Disable rule' : 'Enable rule'}>
                      {rule.is_active ? <ToggleRight size={18} className="text-emerald-600"/> : <ToggleLeft size={18}/>}
                    </button>
                    <button data-testid={`expand-rule-${rule.id}`}
                      onClick={() => {
                        const nowExpanded = !expanded[rule.id];
                        setExpanded(e => ({ ...e, [rule.id]: nowExpanded }));
                        if (nowExpanded && rule.max_users && !seatsData[rule.id]) loadSeats(rule.id);
                      }}
                      className="p-2 rounded-lg hover:bg-white/60 text-gray-500 transition-colors">
                      {isExpanded ? <ChevronUp size={16}/> : <ChevronDown size={16}/>}
                    </button>
                    <button data-testid={`edit-rule-${rule.id}`} onClick={() => openEdit(rule)}
                      className="p-2 rounded-lg hover:bg-white/60 text-gray-600 transition-colors"><Edit2 size={15}/></button>
                    <button data-testid={`delete-rule-${rule.id}`} onClick={() => deleteRule(rule.id)}
                      className="p-2 rounded-lg hover:bg-red-100 text-red-500 transition-colors"><Trash2 size={15}/></button>
                  </div>
                </div>
                {isExpanded && (
                  <div className="px-4 pb-4">
                    <div className="bg-white/70 rounded-lg p-3 space-y-2">
                      <p className="text-xs font-semibold text-gray-600 mb-2">
                        Conditions (match <span className="uppercase font-bold">{rule.match_all ? 'all' : 'any'}</span>):
                      </p>
                      {rule.conditions.map((c, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs bg-white rounded-lg px-3 py-2 border border-gray-100">
                          <span className="font-mono font-medium text-blue-700">{c.field}</span>
                          <span className="text-gray-400">{c.operator.replace(/_/g,' ')}</span>
                          <span className="font-medium text-gray-700 truncate max-w-xs">"{c.value}"</span>
                        </div>
                      ))}
                    </div>

                    {!!rule.max_users && (
                      <div className="mt-3 bg-white/70 rounded-lg p-3" data-testid={`seats-panel-${rule.id}`}>
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-semibold text-gray-600 flex items-center gap-1.5">
                            <Gauge size={13} className="text-indigo-500" />
                            Capacity: {seatsData[rule.id]?.seats_used ?? rule.seats_used ?? 0} / {rule.max_users} seats used
                            {rule.domains?.length > 0 && (
                              <span className="text-gray-400 font-normal ml-1">— {rule.domains.length} domain(s)</span>
                            )}
                          </p>
                          {!seatsData[rule.id] && (
                            <button
                              onClick={() => loadSeats(rule.id)}
                              disabled={loadingSeats[rule.id]}
                              className="text-xs text-indigo-600 hover:underline font-medium"
                              data-testid={`load-seats-${rule.id}`}
                            >
                              {loadingSeats[rule.id] ? 'Loading...' : 'View seat holders'}
                            </button>
                          )}
                        </div>
                        {seatsData[rule.id] && (
                          seatsData[rule.id].seats.length === 0 ? (
                            <p className="text-xs text-gray-400 italic">No seats consumed yet.</p>
                          ) : (
                            <div className="space-y-1 max-h-48 overflow-y-auto">
                              {seatsData[rule.id].seats.map(seat => {
                                const key = `${rule.id}-${seat.email}`;
                                return (
                                  <div key={seat.email} className="flex items-center justify-between text-xs bg-white rounded-lg px-3 py-1.5 border border-gray-100">
                                    <span className="text-gray-700 truncate">{seat.email}</span>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                      <span className="text-gray-400">{seat.granted_at ? new Date(seat.granted_at).toLocaleDateString() : ''}</span>
                                      <button
                                        onClick={() => freeSeat(rule.id, seat.email)}
                                        disabled={freeingSeat === key}
                                        className="text-red-500 hover:text-red-700 hover:underline font-medium"
                                        data-testid={`free-seat-${rule.id}-${seat.email}`}
                                      >
                                        {freeingSeat === key ? 'Freeing...' : 'Free seat'}
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )
                        )}

                        {seatsData[rule.id]?.waitlist?.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-gray-200" data-testid={`waitlist-panel-${rule.id}`}>
                            <p className="text-xs font-semibold text-amber-700 flex items-center gap-1.5 mb-2">
                              <Users size={13} /> Waitlist ({seatsData[rule.id].waitlist.length}) — auto-granted a seat when one frees up
                            </p>
                            <div className="space-y-1 max-h-40 overflow-y-auto">
                              {seatsData[rule.id].waitlist.map(w => {
                                const pkey = `promote-${rule.id}-${w.email}`;
                                const seatsFull = (seatsData[rule.id]?.seats_used ?? rule.seats_used ?? 0) >= rule.max_users;
                                return (
                                  <div key={w.email} className="flex items-center justify-between text-xs bg-amber-50 rounded-lg px-3 py-1.5 border border-amber-100">
                                    <span className="text-gray-700 truncate">{w.email} {w.full_name ? `(${w.full_name})` : ''}</span>
                                    <div className="flex items-center gap-2 flex-shrink-0">
                                      <span className="text-gray-400">{w.requested_at ? new Date(w.requested_at).toLocaleDateString() : ''}</span>
                                      <button
                                        onClick={() => promoteWaitlisted(rule.id, w.email)}
                                        disabled={freeingSeat === pkey || seatsFull}
                                        title={seatsFull ? 'No free seats — free one first or raise Max Users' : 'Grant a seat now'}
                                        className="text-emerald-600 hover:text-emerald-800 hover:underline font-medium disabled:text-gray-400 disabled:no-underline disabled:cursor-not-allowed"
                                        data-testid={`promote-waitlist-${rule.id}-${w.email}`}
                                      >
                                        {freeingSeat === pkey ? 'Granting...' : 'Promote now'}
                                      </button>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Access Rule Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div data-testid="rule-form-modal" className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="font-bold text-lg text-gray-900">
                {editingRule ? 'Edit Access Rule' : 'Create Access Rule'}
              </h2>
              <button onClick={() => setShowForm(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X size={18}/></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Rule Name *</label>
                <input data-testid="rule-name-input" value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Allow Top50 CSP Partners"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-levelup-accent" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Description</label>
                <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Optional explanation"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-levelup-accent" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">Effect *</label>
                  <select data-testid="rule-effect-select" value={form.effect}
                    onChange={e => setForm(f => ({ ...f, effect: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-levelup-accent">
                    <option value="ALLOW">ALLOW — grant access</option>
                    <option value="DENY">DENY — block access</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">Condition Logic</label>
                  <select value={form.match_all ? 'all' : 'any'} onChange={e => setForm(f => ({ ...f, match_all: e.target.value === 'all' }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-levelup-accent">
                    <option value="all">AND — all conditions must match</option>
                    <option value="any">OR — any condition can match</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">Priority (higher = first)</label>
                  <input type="number" value={form.priority} onChange={e => setForm(f => ({ ...f, priority: parseInt(e.target.value, 10) || 0 }))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-levelup-accent" />
                </div>
                <div className="flex items-end pb-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))}
                      className="w-4 h-4 accent-levelup-accent" />
                    <span className="text-sm font-medium text-gray-700">Active</span>
                  </label>
                </div>
              </div>
              {form.effect === 'ALLOW' && (
                <div>
                  <label className="text-sm font-medium text-gray-700 block mb-1">Max Users (optional capacity limit)</label>
                  <input
                    type="number"
                    min="0"
                    data-testid="rule-max-users-input"
                    value={form.max_users}
                    onChange={e => setForm(f => ({ ...f, max_users: e.target.value }))}
                    placeholder="Leave blank for unlimited"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-levelup-accent"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    Caps how many distinct users can ever log in under this rule. Works best when this rule has a single
                    <code className="bg-gray-100 px-1 rounded mx-1">domain</code><code className="bg-gray-100 px-1 rounded">is in</code>
                    condition listing every domain the organization owns (e.g. all 10 Ingram Micro domains) — that whole group
                    then shares one capped pool of seats. Already-active users are automatically grandfathered in.
                  </p>
                </div>
              )}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">Conditions *</label>
                  <button data-testid="add-condition-btn" onClick={addCondition}
                    className="text-xs px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium flex items-center gap-1 transition-colors">
                    <Plus size={13}/> Add Condition
                  </button>
                </div>
                {form.conditions.length === 0 && (
                  <p className="text-sm text-gray-400 italic">No conditions yet. Add at least one.</p>
                )}
                <div className="space-y-2">
                  {form.conditions.map((c, i) => (
                    <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-lg p-2">
                      <select value={c.field} onChange={e => updateCondition(i, 'field', e.target.value)}
                        className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-levelup-accent">
                        {config.token_fields.map(f => (
                          <option key={f.field} value={f.field}>{f.label} ({f.field})</option>
                        ))}
                      </select>
                      <select value={c.operator} onChange={e => updateCondition(i, 'operator', e.target.value)}
                        className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-levelup-accent">
                        {config.operators.map(op => (
                          <option key={op.value} value={op.value}>{op.label}</option>
                        ))}
                      </select>
                      <input value={c.value} onChange={e => updateCondition(i, 'value', e.target.value)}
                        placeholder="value"
                        className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-levelup-accent" />
                      <button onClick={() => removeCondition(i)} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors">
                        <X size={14}/>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 p-5 border-t bg-gray-50 rounded-b-2xl">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-100 transition-colors">Cancel</button>
              <button data-testid="save-rule-btn" onClick={saveRule} disabled={saving}
                className="px-5 py-2 bg-levelup-accent text-levelup-darker rounded-lg text-sm font-bold disabled:opacity-60 hover:opacity-90 transition-opacity">
                {saving ? 'Saving...' : (editingRule ? 'Update Rule' : 'Create Rule')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
});

export default AccessRulesTab;
